import React, { useMemo } from 'react';
import { FilePreview as BaseFilePreview, FilePreviewProps } from '@fx-ui/fine-design';
import { IFile } from '~/interfaces';
import {
    map,
    get,
    getPublicImageUrl,
    parseFileName,
    isImageExt,
    isPreviewImage,
    isOfficeExt,
    isSupportPdf,
    isPdfExt
} from '~/utils';
import { MAX_OFFICE_VIEW_SIZE } from '~/constants';
import UnSupportImagePreview from '../unsupport-image-preview';
import UnSupportFilePreview from '../unsupport-file-preview';
import PdfEmbed from '../pdf-embed';

interface IFilePreviewProps extends Omit<FilePreviewProps, 'files'> {
    files: IFile[];
}

/**
 * 基于组件库FilePreview增加了对Files的格式处理
 *
 * @param root0
 * @param root0.files
 * @param root0.unsupportedFileRender
 * @returns
 */
const FilePreview: React.FC<IFilePreviewProps> = ({
    files,
    unsupportedFileRender = (file) => <UnSupportFilePreview file={file} />,
    ...restProps
}) => {
    const previewFiles = useMemo(() => {
        return map(files, (file) => {
            const name = get(file, 'name', '');
            const ext = parseFileName(name)[1];
            const previewFile = {
                name,
                type: get(file, 'mime', ''),
                url: get(file, 'url', ''),
                downloadUrl: get(file, 'downloadUrl', ''),
                previewUrl:
                    get(file, 'previewUrl', '') ||
                    getPublicImageUrl(file) ||
                    get(file, 'downloadUrl', '')
            };
            if (isImageExt(ext) && !isPreviewImage(file)) {
                return {
                    ...previewFile,
                    fileRender: () => <UnSupportImagePreview />
                };
            }
            if (isOfficeExt(ext)) {
                if (get(file, 'size', 0) > MAX_OFFICE_VIEW_SIZE) {
                    return {
                        ...previewFile,
                        fileRender: () => <UnSupportFilePreview file={file} mode="size" />
                    };
                } else if (!file.previewUrl) {
                    // wps、ofd文件仅部分场景支持预览，判断标准为是否有previewUrl
                    return {
                        ...previewFile,
                        fileRender: () => <UnSupportFilePreview file={file} />
                    };
                }
            }
            // 优先使用浏览器直接预览PDF
            if (isPdfExt(ext) && isSupportPdf() && file.url) {
                return {
                    ...previewFile,
                    fileRender: () => (
                        <div className="preview-center">
                            <PdfEmbed src={file.url} />
                        </div>
                    )
                };
            }
            return previewFile;
        });
    }, [files]);

    return (
        <BaseFilePreview
            files={previewFiles}
            unsupportedFileRender={unsupportedFileRender}
            {...restProps}
        />
    );
};

export default FilePreview;
